import { Box, useTheme } from "@mui/material";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useTranslation } from "react-i18next";
import { useRef, useState, type JSX } from "react";
import { ContextActionMenu } from "../contextual-app-bar/ContextActionMenu";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import AdminSetpointList, { AlarmSetpointListType } from "../admin-setpoint-list/AdminSetpointList";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useLinkSetpoints } from "../../queries/alarm-setpoint-admin/UseLinkSetpoints";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { isMobile, isTablet } from "react-device-detect";
import { SetPointListActionEnum, contextActionItems } from "../admin-setpoint-list/AdminSetpointListUtils";
import { ActionMenuItem, BaseActions } from "../action-menu/ActionMenuItem";


export type LinkExistingSetpointDialogInitValues = {
    linkedSetpoints: number[]
};

export type LinkExistingSetpointDialogResultValues = {
    tagID: number | undefined;
};

export type LinkExistingSetpointDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type LinkExistingSetpointDialogResult = DialogResult<LinkExistingSetpointDialogResultValues, LinkExistingSetpointDialogResultReason>;

export type LinkExistingSetpointDialogProps = DialogProps<LinkExistingSetpointDialogInitValues, LinkExistingSetpointDialogResult>;

export default function LinkExistingSetpointDialog({
    open,
    onClose,
    values
}: LinkExistingSetpointDialogProps): JSX.Element {
    
    const isPhone = isMobile && !isTablet;
    
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false : true);
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const theme = useTheme();
    const routeContext = useRouteContext();

    const setpointListRef = useRef<AlarmSetpointListType>(undefined);

    const { mutate: linkTagToSetpoints/*, status*/ } = useQueryNotifications(useLinkSetpoints)({
        groupID: routeContext?.groupID as number,
        tagID: routeContext?.tagID as number,
        onSuccess: async () => {
            acknowledge(t("setpointAdmin.successLinked"));

            onClose({
                reason: "save"
            });
        }
    });

    const isDirty = selectedItems.length > 0;

    async function onOkay() {

        const response =  await confirm({
            dialogTitle: t("actions.linkToSetpoint"),
            confirmationText: t("setpointAdmin.linkTagQuestion"),
            yesButtonText: t("buttons.yesLink"),
            noButtonText: t("buttons.noCancel"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });

        if (response === 'yes'){

            linkTagToSetpoints(selectedItems);
        }
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            setSelectedItems([]);
            onClose && onClose({ reason: "cancel" });
        }
    }

    function onSelected(payload: number[]) {
        setSelectedItems(payload);
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        const action = menuItem.action as SetPointListActionEnum | BaseActions;
        setpointListRef.current?.executeAction(action);
    }

    return (
        <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>
            <SettingsBar title='Link to Existing Setpoint' 
                        onClose={
                            async (reason: SettingsActionEnum) => {
                                reason === SettingsActionEnum.OK ? 
                                    onOkay() :
                                    onCancel();
                            }}
                        modified={isDirty} 
                        />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    position: 'relative'
                }}>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 1 auto',
                        overflow: 'hidden',
                        overflowY: 'auto',
                        marginTop: theme.spacing(6)
                    }}>

                    <AdminSetpointList 
                        ref={setpointListRef}
                        groupID={routeContext.groupID as number} 
                        selectable={true}
                        linkedSetpoints={values?.linkedSetpoints}
                        onSelected={onSelected} 
                        openSideSheet={openSideSheet}
                        onCloseSideSheet={() => {setOpenSideSheet(false)}}
                        setViewOptionState={setViewOptionState}
                        />
                </Box>
            
                {/* // NOTE this has to position absolute, */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: theme.spacing(3)
                    }}>
                <ContextActionMenu 
                    actions={contextActionItems}
                    viewOptionState={viewOptionState}
                    onExecuteAction={onExecuteAction}
                    onOverrideViewOptionsClick={() => {setOpenSideSheet(!openSideSheet)}}
                    buttonColor="default"
                    hideAddToFavoritesAction={true}
                    />
                </Box>


            </Box>
        </FullScreenDialog>
    );
}

export type LinkExistingSetpointDialogState = DialogState<LinkExistingSetpointDialogInitValues, LinkExistingSetpointDialogResult>;
export type BindLinkExistingSetpointDialogReturn = BindDialogReturn<LinkExistingSetpointDialogInitValues, LinkExistingSetpointDialogResult>;

export function useLinkExistingSetpointDialogState(): LinkExistingSetpointDialogState {
    return useDialogState();
}

export function bindLinkExistingSetpointDialog(props: LinkExistingSetpointDialogState): BindLinkExistingSetpointDialogReturn {
    return bindDialog(props);
}
